import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useShiftSiteAttendanceApiService } from "../services/api/ShiftSiteAttendanceApiService";
import {
  ShiftSiteAttendanceLocationGroup,
  ShiftSiteAttendanceResponse,
} from "../services/model/ShiftSite";
import { ShiftConfig } from "../services/model/ShiftLocation";
import { EmployeeShiftRecord } from "../services/model/EmployeeShiftRecord";
import {
  StatusFilterValue,
  VALID_STATUS_FILTERS,
} from "../utils/statusFilterConfig";
import {
  getActiveShiftConfig,
  computeAttendanceCounts,
} from "../utils/attendanceUtils";

interface ShiftSiteAttendanceStats {
  totalOnSite: number;
  totalScheduled: number;
  coveragePercent: number | null;
  presentNow: number;
}

interface UseShiftSiteAttendanceResult {
  site: ShiftSiteAttendanceResponse["site"] | null;
  shiftLocations: ShiftSiteAttendanceLocationGroup[];
  filteredShiftLocations: ShiftSiteAttendanceLocationGroup[];
  availableRosters: string[];
  availableShifts: string[];
  shiftConfigs: ShiftConfig[];
  rosterFilter: string;
  setRosterFilter: (val: string) => void;
  shiftFilter: string;
  setShiftFilter: (val: string) => void;
  statusFilters: StatusFilterValue[];
  setStatusFilters: (val: StatusFilterValue[]) => void;
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  date: string;
  setDate: (val: string) => void;
  isLoading: boolean;
  error: Error | null;
  stats: ShiftSiteAttendanceStats;
}

// Local calendar date (YYYY-MM-DD). Uses dayjs (local time), matching
// useShiftSiteDetail and dateRangeUtils — a UTC date (toISOString) would flip a
// day early/late around local midnight, defaulting the view to the wrong day and
// mis-evaluating the today-only polling check below.
const today = (): string => dayjs().format("YYYY-MM-DD");

const matchesStatus = (
  record: EmployeeShiftRecord,
  statusFilters: StatusFilterValue[],
  lateThreshold: number
): boolean => {
  if (statusFilters.length === 0) return true;

  return statusFilters.some((filter) => {
    switch (filter) {
      case "on_time":
        return (
          record.clock_in_time &&
          record.late_minutes !== null &&
          record.late_minutes >= 0 &&
          record.late_minutes <= lateThreshold &&
          record.status !== "no_show"
        );
      case "late_arrival":
        return (
          record.late_minutes != null && record.late_minutes > lateThreshold
        );
      case "no_show":
        return record.status === "no_show";
      case "no_data":
        return record.status === "not_clocked_in";
      case "early":
        return record.late_minutes !== null && record.late_minutes < 0;
      case "on_site":
        return record.status === "active";
      case "supervisor":
        return (
          record.clock_in_source === "supervisor" ||
          record.clock_out_source === "supervisor"
        );
      case "admin":
        return (
          record.clock_in_source === "admin" ||
          record.clock_out_source === "admin"
        );
      case "replacement":
        return record.is_replacement;
      case "unscheduled":
        return record.is_unscheduled;
      default:
        return false;
    }
  });
};

const useShiftSiteAttendance = (
  siteId: string
): UseShiftSiteAttendanceResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [rosterFilter, setRosterFilter] = useState(
    searchParams.get("roster") || "all"
  );
  const [shiftFilter, setShiftFilter] = useState(
    searchParams.get("shift") || "all"
  );
  const [statusFilters, setStatusFilters] = useState<StatusFilterValue[]>(
    () => {
      const raw = searchParams.get("status");
      if (!raw) return [];
      return raw
        .split(",")
        .filter((s) =>
          VALID_STATUS_FILTERS.has(s as StatusFilterValue)
        ) as StatusFilterValue[];
    }
  );
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [date, setDate] = useState(searchParams.get("date") || today());

  // URL sync helpers
  const updateParam = (key: string, value: string | null): void => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params, { replace: true });
  };

  const handleSetRosterFilter = (val: string): void => {
    setRosterFilter(val);
    updateParam("roster", val === "all" ? null : val);
  };

  const handleSetShiftFilter = (val: string): void => {
    setShiftFilter(val);
    updateParam("shift", val === "all" ? null : val);
  };

  const handleSetStatusFilters = (val: StatusFilterValue[]): void => {
    setStatusFilters(val);
    updateParam("status", val.length > 0 ? val.join(",") : null);
  };

  const handleSetSearchQuery = (val: string): void => {
    setSearchQuery(val);
    updateParam("q", val || null);
  };

  const handleSetDate = (val: string): void => {
    setDate(val);
    updateParam("date", val);
  };

  // API call — poll every 30s on the live "today" view so duty/post grouping
  // reflects out-of-band clock-ins (mobile/WhatsApp) and roster changes that
  // don't invalidate this query. Historical dates never poll.
  const isTodayView = date === today();
  const { data, isLoading, error } = useShiftSiteAttendanceApiService({
    siteId,
    date,
    enabled: !!siteId,
    refetchInterval: isTodayView ? 30000 : false,
  });

  const site = data?.site || null;
  const shiftLocations = data?.shift_locations || [];
  const availableRosters = data?.filters?.rosters || [];
  const availableShifts = data?.filters?.shifts || [];

  // Aggregate unique shift configs from all location groups (dedup by name).
  // When the same shift name appears on multiple locations, prefer the variant
  // with non-null start/end times so downstream time-based logic has data to work with.
  const shiftConfigs = useMemo(() => {
    const seen = new Map<string, ShiftConfig>();
    shiftLocations.forEach((group) => {
      (group.location.shift_configs || []).forEach((config) => {
        const existing = seen.get(config.name);
        if (!existing) {
          seen.set(config.name, config);
          return;
        }
        const existingHasTimes = Boolean(
          existing.start_time && existing.end_time
        );
        const candidateHasTimes = Boolean(config.start_time && config.end_time);
        if (!existingHasTimes && candidateHasTimes) {
          seen.set(config.name, config);
        }
      });
    });
    return Array.from(seen.values());
  }, [shiftLocations]);

  // Build a set of all shift_config_ids that match the selected shift name
  // (different locations may have different IDs for the same named shift)
  const activeShiftConfigIds = useMemo(() => {
    if (shiftFilter === "all") return null;
    const selectedConfig = shiftConfigs.find((c) => c.id === shiftFilter);
    if (!selectedConfig) return new Set([shiftFilter]);
    const ids = new Set<string>();
    shiftLocations.forEach((group) => {
      (group.location.shift_configs || []).forEach((config) => {
        if (config.name === selectedConfig.name) {
          ids.add(config.id);
        }
      });
    });
    return ids;
  }, [shiftFilter, shiftConfigs, shiftLocations]);

  // Auto-select the current active shift on first data load
  const hasAutoSelected = useRef(false);
  const initialShiftParam = useRef(searchParams.get("shift"));

  useEffect(() => {
    if (
      hasAutoSelected.current ||
      initialShiftParam.current ||
      isLoading ||
      shiftConfigs.length === 0 ||
      // The active shift is a "right now" concept — only default to it for the
      // live today view. Past days default to all shifts (the whole day).
      !isTodayView
    )
      return;
    const activeId = getActiveShiftConfig(shiftConfigs);
    if (activeId) {
      setShiftFilter(activeId);
      setSearchParams(
        (prev) => {
          prev.set("shift", activeId);
          return prev;
        },
        { replace: true }
      );
    }
    hasAutoSelected.current = true;
  }, [isLoading, shiftConfigs, setSearchParams, isTodayView]);

  // Filtering
  const filteredShiftLocations = useMemo(() => {
    let groups = [...shiftLocations];

    // 1. Roster filter: remove whole groups
    if (rosterFilter !== "all") {
      groups = groups.filter((g) => g.location.name === rosterFilter);
    }

    // 2. Within each group, filter records
    const q = searchQuery.toLowerCase();
    groups = groups
      .map((group) => {
        let records = [...group.records];

        // Shift filter (replacements always pass through)
        if (shiftFilter !== "all" && activeShiftConfigIds) {
          records = records.filter(
            (r) =>
              (r.shift_config_id &&
                activeShiftConfigIds.has(r.shift_config_id)) ||
              r.is_replacement === true
          );
        }

        // Status filter
        if (statusFilters.length > 0) {
          const locThreshold =
            group.location.late_arrival_threshold_minutes ?? 5;
          records = records.filter((r) =>
            matchesStatus(r, statusFilters, locThreshold)
          );
        }

        // Search filter
        if (q) {
          records = records.filter((r) =>
            r.employee_name.toLowerCase().includes(q)
          );
        }

        // Group replaced + replacement pairs adjacently
        const replacementByTarget = new Map<number, EmployeeShiftRecord>();
        records.forEach((r) => {
          if (r.is_replacement && r.covered_employee_id) {
            replacementByTarget.set(r.covered_employee_id, r);
          }
        });

        if (replacementByTarget.size > 0) {
          const replacementRecords = new Set(replacementByTarget.values());
          const withoutReplacements = records.filter(
            (r) => !replacementRecords.has(r)
          );
          const grouped: EmployeeShiftRecord[] = [];
          const inserted = new Set<EmployeeShiftRecord>();
          withoutReplacements.forEach((r) => {
            grouped.push(r);
            if (r.is_replaced) {
              const replacement = replacementByTarget.get(r.employee_id);
              if (replacement) {
                grouped.push(replacement);
                inserted.add(replacement);
              }
            }
          });
          // Append orphaned replacements
          replacementRecords.forEach((r) => {
            if (!inserted.has(r)) grouped.push(r);
          });
          records = grouped;
        }

        return { ...group, records };
      })
      // 3. Remove empty groups
      .filter((g) => g.records.length > 0);

    return groups;
  }, [
    shiftLocations,
    rosterFilter,
    shiftFilter,
    activeShiftConfigIds,
    statusFilters,
    searchQuery,
  ]);

  // Stats derived from filtered records. Coverage semantics: the headline
  // "X of Y" is covered scheduled slots over scheduled slots — a no-show whose
  // slot a reliever filled still counts as covered, and off-roster relievers no
  // longer inflate the denominator. presentNow is the live "on site right now"
  // secondary figure (only surfaced on the today view).
  const stats = useMemo(() => {
    const allRecords = filteredShiftLocations.flatMap((group) => group.records);
    const counts = computeAttendanceCounts(allRecords);
    const coveragePercent =
      counts.scheduled > 0
        ? Math.min(100, Math.round((counts.covered / counts.scheduled) * 100))
        : null;
    return {
      totalOnSite: counts.covered,
      totalScheduled: counts.scheduled,
      coveragePercent,
      presentNow: counts.presentNow,
    };
  }, [filteredShiftLocations]);

  return {
    site,
    shiftLocations,
    filteredShiftLocations,
    availableRosters,
    availableShifts,
    shiftConfigs,
    rosterFilter,
    setRosterFilter: handleSetRosterFilter,
    shiftFilter,
    setShiftFilter: handleSetShiftFilter,
    statusFilters,
    setStatusFilters: handleSetStatusFilters,
    searchQuery,
    setSearchQuery: handleSetSearchQuery,
    date,
    setDate: handleSetDate,
    isLoading,
    error,
    stats,
  };
};

export default useShiftSiteAttendance;
